import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Sparkles } from 'lucide-react';
import { companyInfo } from '../mock';

const Hero = () => {
  const { t } = useTranslation();
  
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const targetAudiences = [
    { id: 1, title: t('targetAudience.hospitals'), description: t('targetAudience.hospitalsDesc') },
    { id: 2, title: t('targetAudience.practitioners'), description: t('targetAudience.practitionersDesc') },
    { id: 3, title: t('targetAudience.wellness'), description: t('targetAudience.wellnessDesc') },
  ];

  return (
    <section
      id="hero"
      style={{
        background: '#0A1628',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '7rem 1.2rem 3rem',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Main Banner Background - Nature/Tech Fusion */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_medherb-tech/artifacts/c40van61_Concept-1%20IMAGE_Desktop%20%281%29.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.25,
          zIndex: 0,
        }}
      />
      
      {/* Dark Overlay for Text Readability */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, rgba(10, 22, 40, 0.3) 0%, transparent 40%, transparent 60%, rgba(10, 22, 40, 0.3) 100%)',
          zIndex: 1,
        }}
      />
      <div className="container">
        <div className="hero-content-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '3rem', alignItems: 'center', position: 'relative', zIndex: 2 }}>
          {/* Left Content Section */}
          <div className="hero-left-content" style={{ textAlign: 'left' }}>
            {/* Announcement Badge */}
            <div
              className="hero-badge"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'rgba(34, 197, 94, 0.2)',
                borderRadius: '2rem',
                padding: '0.5rem 1rem',
                marginBottom: '2rem',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(34, 197, 94, 0.4)',
                boxShadow: '0 0 20px rgba(34, 197, 94, 0.3)',
              }}
            >
              <Sparkles size={16} color="var(--tech-green-400)" />
              <span
                style={{
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em',
                  fontWeight: '600',
                  color: 'var(--tech-green-300)',
                }}
              >
                {t('hero.badge')}
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="heading-hero" style={{ marginBottom: '1.5rem', color: 'var(--text-light)' }}>
              {t('hero.title')}
            </h1>

            {/* Description */}
            <p className="body-large" style={{ marginBottom: '2.5rem', color: 'rgba(255, 255, 255, 0.9)' }}>
              {t('hero.description')}
            </p>

            {/* CTA Buttons */}
            <div
              className="hero-cta-buttons"
              style={{
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap',
                marginBottom: '2rem',
              }}
            >
              <button
                onClick={() => scrollToSection('contact')}
                className="btn-primary hover-scale"
                style={{ gap: '0.5rem' }}
              >
                {t('hero.getStarted')} <ArrowRight size={16} />
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="btn-secondary hover-scale"
                style={{ color: '#ffffff', borderColor: 'rgba(34, 197, 94, 0.6)' }}
              >
                {t('hero.exploreServices')}
              </button>
            </div>
          </div>

          {/* Right Content Section - Doctor Image */}
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div
              style={{
                position: 'relative',
                maxWidth: '500px',
                width: '100%',
              }}
            >
              {/* Glowing Circle Background */}
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '400px',
                  height: '400px',
                  background: 'radial-gradient(circle, rgba(34, 197, 94, 0.3) 0%, transparent 70%)',
                  borderRadius: '50%',
                  filter: 'blur(40px)',
                  zIndex: 0,
                }}
              />
              
              {/* Doctor Image */}
              <img
                src="https://customer-assets.emergentagent.com/job_medherb-tech/artifacts/m72vbmll_01.jpg"
                alt="Healthcare Professional"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '1rem',
                  position: 'relative',
                  zIndex: 1,
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(34, 197, 94, 0.4)',
                  border: '2px solid rgba(34, 197, 94, 0.5)',
                }}
              />
            </div>
          </div>
        </div>

        {/* Target Audiences - Below the main hero */}
        <div
          className="hero-target-audiences"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1.5rem',
            marginTop: '4rem',
            position: 'relative',
            zIndex: 2,
          }}
        >
            {targetAudiences.map((audience) => (
              <div
                key={audience.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  textAlign: 'left',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 20px rgba(34, 197, 94, 0.1)',
                }}
                className="hover-lift"
              >
                <h3 className="heading-3" style={{ marginBottom: '0.5rem', color: 'var(--tech-green-300)' }}>
                  {audience.title}
                </h3>
                <p className="body-small" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {audience.description}
                </p>
              </div>
            ))}
          </div>
      </div>
    </section>
  );
};

export default Hero;
